/*
 * Authors: mariushirjanu
 * Created: 22/08/2022
 */
import * as _ from 'underscore';
import { Subscription } from 'rxjs';
import { GlobalProductService } from '../global-product.service';
import { GlobalProductDataService, IGlobalItem, IProperty, IPropertyGroup } from '../global-product-data.service';
import { STATE_GLOBALPRODUCT_EDIT_GENERAL } from '../../../shared/app-states';
import { GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { EVENT_GLOBAL_PRODUCT_IMAGES_LOADED } from '../../../shared/app-events';
import { UrlService } from '../../../shared/browser/url.service';
import { util } from '../../../shared/util';
import { IDescribedNamedObject } from '../../../../type_definitions/datatypes';
import { Component, OnDestroy, OnInit, Input } from '@angular/core';

@Component({
  selector: 'global-product-view',
  template: `
    <div class="globalproduct-content" *ngIf="!newProduct">
      <div class="catalog-detail">

        <div class="top-container">
          <div class="row">
            <div class="col-sm-8 title">
              <div class="badge partnumber">{{model.globalPart}}</div>
            </div>
            <div class="col-sm-4">
              <a class="btn btn-primary globalproduct-edit" uiSref="${STATE_GLOBALPRODUCT_EDIT_GENERAL}">Edit</a>
            </div>
          </div>
        </div>

        <div class="content">
          <div class="row description-block">

            <div *ngIf="displayImages" class="images col-sm-6 col-sm-pull-6">
              <slick [images]="displayImages" [thumbs]="displayImages"></slick>
            </div>

            <div [ngClass]="{'col-sm-6 col-sm-push-6': displayImages, 'col-sm-12 col-sm-push-12': !displayImages}">
              <div class="description" [innerHTML]="model.description"></div>
            </div>

          </div>

          <h3>Technical Specifications</h3>
          <table class="table specs">
            <tbody>
              <tr class="header">
                <td colspan="2"><h4>Product</h4></td>
              </tr>
              <tr class="even">
                <td class="name">Description</td>
                <td [innerHTML]="model.description"></td>
              </tr>
              <tr>
                <td class="name">Category</td>
                <td>{{model.category?.description}}</td>
              </tr>
              <tr>
                <td class="name">Category Detail</td>
                <td>{{model.subcategory?.name}}</td>
              </tr>
              <tr class="even">
                <td class="name">Vendor</td>
                <td>{{model.vendor?.name}}</td>
              </tr>
              <tr>
                <td class="name">Short Description</td>
                <td>{{model.shortDescription}}</td>
              </tr>
              <tr>
                <td class="name">Global Part Number</td>
                <td>
                  <div class="badge partnumber">{{model.globalPart}}</div>
                </td>
              </tr>
              <tr class="even website">
                <td class="name">Website</td>
                <td><a href="{{model.url}}" target="_blank">{{model.url}}</a></td>
              </tr>
              <tr>
                <td class="name">Documentation</td>
                <td>
                  <div><a [href]="documentUrl" target="_blank">{{model.document?.description}}</a></div>
                </td>
              </tr>

              <ng-container *ngIf="!loadingProperties">
                <ng-container *ngFor="let propGroup of model.properties; trackById">
                  <tr class="header">
                    <td colspan="2">
                      <h4>{{ propGroup.name }}</h4>
                    </td>
                  </tr>

                  <tr *ngFor="let value of propGroup.values; let index = index; trackById" [ngClass]="{even: index%2 === 0}">
                    <td class="name">{{value?.name}}</td>
                    <td>{{ formatValue(value, propGroup.name) }} <span *ngIf="value.unit">{{value.unit.description}}</span></td>
                  </tr>
                </ng-container>
              </ng-container>


              <tr *ngIf="model.freeProperties" class="header">
                <td colspan="2">
                  <h4>Custom Properties</h4>
                </td>
              </tr>

              <ng-container *ngIf="model.freeProperties">
                <tr *ngFor="let freeProp of model.freeProperties; let index = index; trackById" [ngClass]="{even: index%2 === 0}">
                  <td class="name">{{ freeProp.name }}</td>
                  <td>{{ freeProp.value }}</td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>

      </div>

      <div *ngIf="loading" class="catalog-detail-overlay">
        <div class="bg"></div>
        <div class="text">
          <i class="fa fa-spinner fa-spin"></i>
          <div>&nbsp;loading…</div>
        </div>
      </div>

    </div>
  `
})
export class GlobalProductViewComponent implements OnInit, OnDestroy {
  @Input() public newProduct:boolean;

  public model:IGlobalItem;
  private properties:IPropertyGroup[];
  public loading:boolean = true;
  public loadingProperties:boolean;
  public displayImages:string[];
  private eventHandler:Subscription;

  constructor(
    private gaEventService:GaEventService,
    private globalProductDataService:GlobalProductDataService,
    private globalProductService:GlobalProductService,
    private urlService:UrlService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.start();
    this.listen();
  }

  private start = ():void => {
    this.model = this.globalProductService.product() || <any>{};
    this.setImages();
    this.loadProperties();
  };

  private listen():void {
    this.eventHandler = this.gaEventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_GLOBAL_PRODUCT_IMAGES_LOADED) {
        this.setImages();
      }
    });
  }

  private loadProperties():void {
    this.loadingProperties = true;
    this.globalProductDataService.properties()
      .then((properties:IPropertyGroup[]) => {
        this.globalProductService.properties(properties);
        this.properties = this.globalProductService.properties();
      })
      .finally(() => {
        this.loadingProperties = false;
      });
  }

  private setImages():void {
    this.displayImages = this.globalProductService.displayImages();
    if(this.globalProductService.imagesLoaded){
      this.loading = false;
    }
  }

  public get documentUrl():string{
    return this.urlService.getAttachmentUrl(this.model.document?.id);
  }

  public formatValue(property:IProperty, group:string):string|number|boolean {
    if (util.notNullOrUndefinedOrEmpty(property?.value)) {
      if(_.isBoolean(property.value)){
        return property.value ? 'yes' : 'no';
      } else if(property.control === 'LIST'){
        return this.extractValueFromPropertyList(property, group);
      } else {
        return property.value;
      }
    }
  }

  private extractValueFromPropertyList(p:IProperty, group:string):string {
    const propertyGroup:IPropertyGroup = _.findWhere(this.properties, { name: group });
    const property:IProperty = _.findWhere(propertyGroup.values, { name: p.name });
    const propertyValue:IDescribedNamedObject = _.findWhere(property.values, { value: p.value });
    return propertyValue.description;
  }

  public ngOnDestroy():void {
    this.eventHandler.unsubscribe();
  }
}